import * as React from "react"
import { Head, useForm, usePage } from "@inertiajs/react"
import { ExternalLink, Upload } from "lucide-react"
import { AdminShell } from "@/components/AdminShell"
import { AdminPageHeader, AdminSection } from "@/components/admin/AdminPageHeader"
import { Field, fieldAria } from "@/components/admin/Field"
import { formatBytes } from "@/components/admin/format"
import { Button } from "@/components/ui/button"
import { DataRow, DataTable } from "@/components/ui/data-table"
import { Input } from "@/components/ui/input"
import { formatDayTime } from "@/lib/dates"
import type { AdminSettingsProps, PageProps } from "@/types/inertia"

const TITLE = "Settings"
const DESCRIPTION =
  "Site settings for SOH GD Wines: swap the brochure PDF that visitors download through the email gate."

export default function AdminSettings() {
  const { props } = usePage<PageProps<AdminSettingsProps>>()
  const { brochure, brochure_max_bytes: maxBytes, update_url: updateUrl } = props
  const form = useForm<{ brochure_pdf: File | null }>({ brochure_pdf: null })
  const [tooLarge, setTooLarge] = React.useState(false)
  const error = tooLarge ? `Please upload a PDF of ${formatBytes(maxBytes)} or smaller` : form.errors.brochure_pdf

  const choose = (file: File | null) => {
    // Checked here too, so a file over the limit is never sent.
    setTooLarge(Boolean(file && file.size > maxBytes))
    form.setData("brochure_pdf", file)
  }

  const submit = (event: React.FormEvent) => {
    event.preventDefault()
    if (tooLarge) return
    form.transform((data) => ({ ...data, _method: "patch" }))
    form.post(updateUrl, { forceFormData: true, preserveState: "errors" })
  }

  return (
    <>
      <Head title={TITLE}>
        <meta name="description" content={DESCRIPTION} />
        <meta property="og:title" content={TITLE} />
        <meta property="og:description" content={DESCRIPTION} />
      </Head>
      <AdminShell>
        <AdminPageHeader title="Settings" description="Site-wide settings." />

        <AdminSection
          id="settings-brochure"
          title="Brochure"
          description="The PDF visitors download through the email gate. Each download records a lead and emails them a copy."
        >
          {brochure ? (
            <DataTable>
              <DataRow title="File">
                <span className="break-all">{brochure.filename}</span>
              </DataRow>
              <DataRow title="Size">{formatBytes(brochure.byte_size)}</DataRow>
              <DataRow title="Uploaded">{formatDayTime(brochure.uploaded_at)}</DataRow>
              <DataRow title="Preview">
                <a href={brochure.url} target="_blank" rel="noopener noreferrer" className="inline-flex min-h-11 items-center gap-2">
                  <ExternalLink className="h-4 w-4" aria-hidden="true" />
                  Open the current brochure
                </a>
              </DataRow>
            </DataTable>
          ) : (
            <div className="callout callout-signal">
              <p>No brochure yet, so the brochure links are hidden on the site. Upload a PDF to show them.</p>
            </div>
          )}

          <form onSubmit={submit} className="mt-8 max-w-2xl space-y-4" noValidate>
            <Field
              id="settings-brochure-pdf"
              label={brochure ? "Replace the brochure" : "Upload the brochure"}
              error={error}
              hint={`PDF only, up to ${formatBytes(maxBytes)}. Visitors receive it from the next download on.`}
            >
              <Input
                {...fieldAria("settings-brochure-pdf", { error, hint: true })}
                type="file"
                accept="application/pdf,.pdf"
                className="form-control-large"
                onChange={(event) => choose(event.target.files?.[0] ?? null)}
              />
            </Field>
            <Button type="submit" size="lg" disabled={!form.data.brochure_pdf || tooLarge || form.processing}>
              <Upload className="h-4 w-4" aria-hidden="true" />
              {form.processing ? "Uploading…" : "Upload brochure"}
            </Button>
          </form>
        </AdminSection>
      </AdminShell>
    </>
  )
}
